import { apiClient, API_ENDPOINTS } from '../config';
import { User, AuthSession } from '../types';

/**
 * Authentication Service for Better Auth
 * Uses cookie-based session authentication
 */
export class AuthService {
  /**
   * Sign in with email and password
   */
  static async signIn(email: string, password: string): Promise<User> {
    const response = await apiClient.post<AuthSession>(
      API_ENDPOINTS.auth.signIn,
      { email, password }
    );
    return response.data.user;
  }

  /**
   * Sign out - clears session cookie
   */
  static async signOut(): Promise<void> {
    try {
      // Better Auth requires application/json, send empty object
      await apiClient.post(API_ENDPOINTS.auth.signOut, {});
    } catch (error) {
      // Continue with logout even if API call fails
      console.warn('Sign out API call failed:', error);
    }
  }

  /**
   * Get current session (user + session data)
   */
  static async getSession(): Promise<AuthSession | null> {
    try {
      const response = await apiClient.get<AuthSession>(API_ENDPOINTS.auth.getSession);
      return response.data;
    } catch (error) {
      return null;
    }
  }

  /**
   * Get current user data
   */
  static async getCurrentUser(): Promise<User | null> {
    try {
      const response = await apiClient.get<{ data: User }>(API_ENDPOINTS.auth.me);
      return response.data.data;
    } catch (error) {
      return null;
    }
  }

  /**
   * Check if user is authenticated (has valid session)
   */
  static async isAuthenticated(): Promise<boolean> {
    const session = await AuthService.getSession();
    return session !== null && session.user !== null;
  }

  /**
   * Check if user has one of the specified roles.
   *
   * Global Admins bypass org-level role checks — they are JustPlay-internal
   * staff with platform-wide authority and should never be gated out of an
   * admin page by their org-membership role. The API enforces the same rule
   * via tenant.isGlobalAdmin in middleware/multiTenant.ts; without this
   * bypass on the client, a freshly-promoted Global Admin (role='viewer',
   * global_role='global_admin') gets "Access Denied" before ever hitting
   * the API.
   */
  static hasRole(user: User | null, roles: string[]): boolean {
    if (!user) return false;
    if (user.global_role === 'global_admin') return true;
    return roles.includes(user.role);
  }

  /**
   * Check if user is admin
   */
  static isAdmin(user: User | null): boolean {
    return AuthService.hasRole(user, ['admin']);
  }

  /**
   * Check if user can edit content (admin or editor)
   */
  static canEdit(user: User | null): boolean {
    return AuthService.hasRole(user, ['admin', 'editor']);
  }
}
